import { DateTime } from 'luxon';
import { FC } from 'react';
import styled from 'styled-components';

import { ReactComponent as FileIcon } from '@/assets/icons/file-text.svg';
import Text from '@/components/Text';
import { EditorialFacebookPostResource } from '@/types/resources';
import { EditorialFacebookPostType } from '@/utils/enums';

const Box = styled.div`
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  background: ${({ theme }) => theme.colors.white};
  margin-bottom: 32px;
`;

const BoxInner = styled.div`
  position: relative;
  display: grid;
  grid-gap: 24px 48px;
  grid-template-columns: 1fr 1fr;
  padding: 24px;
`;

const Divider = styled.div`
  position: absolute;
  left: 50%;
  top: 24px;
  bottom: 24px;
  width: 1px;
  background-color: ${({ theme }) => theme.colors.lightGray};
`;

const StyledText = styled(Text)`
  display: flex;
  line-height: 22px;
  margin-top: 6px;
  align-items: center;
  gap: 6px;
`;

const StyledFileIcon = styled(FileIcon)`
  color: ${({ theme }) => theme.colors.gray};
  background-color: ${({ theme }) => theme.colors.grayscaleLight};
`;

interface Props {
  post: EditorialFacebookPostResource;
}

const InfoBox: FC<Props> = ({ post }) => {
  const renderTypeLabel = () => {
    if (post.type === EditorialFacebookPostType.LINK) {
      return 'Link';
    }

    if (post.type === EditorialFacebookPostType.IMAGE) {
      return 'Image';
    }

    if (post.type === EditorialFacebookPostType.VIDEO) {
      return 'Video';
    }

    if (post.type === EditorialFacebookPostType.REEL) {
      return 'Reel';
    }

    return '-';
  };

  return (
    <Box>
      <BoxInner>
        <Divider />
        <div>
          <Text $type="smallBold" $color="gray">
            Message
          </Text>
          <StyledText $type="caption">{post.message}</StyledText>
        </div>
        <div>
          <Text $type="smallBold" $color="gray">
            Page Name
          </Text>
          <StyledText $type="caption">{post.channel.name}</StyledText>
        </div>
        <div>
          <Text $type="smallBold" $color="gray">
            Date Posted
          </Text>
          <StyledText $type="caption">
            {DateTime.fromISO(post.publishedAt).toFormat('yyyy-LL-dd')}
          </StyledText>
        </div>
        <div>
          <Text $type="smallBold" $color="gray">
            Content Type
          </Text>
          <StyledText $type="caption">
            <StyledFileIcon />
            {renderTypeLabel()}
          </StyledText>
        </div>
        <div>
          <Text $type="smallBold" $color="gray">
            Distribution Type
          </Text>
          <StyledText $type="caption">
            {post.queueStatus ? 'Queued' : 'Manual'}
          </StyledText>
        </div>
        <div>
          <Text $type="smallBold" $color="gray">
            Comments
          </Text>
          <StyledText $type="caption">{post.comments ?? '-'}</StyledText>
        </div>
        <div>
          <Text $type="smallBold" $color="gray">
            Post Link
          </Text>
          <StyledText
            as="a"
            target="_blank"
            href={post.facebookLink}
            $type="caption"
            rel="noopener noreferrer"
          >
            {post.facebookLink}
          </StyledText>
        </div>
        <div>
          <Text $type="smallBold" $color="gray">
            Owner
          </Text>
          <StyledText $type="caption">{post.owner}</StyledText>
        </div>
        {!!post.link && (
          <div>
            <Text $type="smallBold" $color="gray">
              Article Link
            </Text>
            <StyledText
              as="a"
              target="_blank"
              href={post.link}
              $type="caption"
              rel="noopener noreferrer"
            >
              {post.link}
            </StyledText>
          </div>
        )}
      </BoxInner>
    </Box>
  );
};

export default InfoBox;
